import { ProductComparisonCommand } from '@app/components/comparisons/components/product-comparison-command';
import PageError from '@app/components/layout/page/components/page-error';
import { PageHeader } from '@app/components/layout/page/components/page-header';
import Page from '@app/components/layout/page/page';
import { pcfRoutes } from '@app/pcf/pcf-routes';
import {
  useGetProduct,
  useGetProductResult,
} from '@shared/api/hooks/pcf/products';
import { StatusEnum } from '@shared/api/types';
import Alert from '@shared/components/alert';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import { Separator } from '@shared/components/ui/separator';
import { capitalizeFirstLetter, formatDate } from '@shared/lib/utils';
import sentry from '@shared/services/sentry';
import { QueryStatus } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import { ProductDetailCarbonRatingCard } from './components/product-detail-carbon-rating-card';
import { ProductDetailCommunicateResults } from './components/product-detail-communicate-results';
import { ProductDetailEmissionsOverTime } from './components/product-detail-emissions-over-time';
import ProductDetailCollections from './components/product-detail-header-collections';
import ProductDetailLifecycleStageEmissionsSection from './components/product-detail-lifecycle-stage-emissions-section';
import ProductDetailName from './components/product-detail-name';
import ProductDetailPageToolbar from './components/product-detail-page-toolbar';
import { ProductDetailTotalEmissionCard } from './components/product-detail-total-emission-card';
import ProductDetailIngredientsSection from './ingredients/product-detail-ingredients-section';
import ProductDetailPackagingSection from './packaging/product-detail-packaging-section';
import ProductDetailProcessingSection from './processing/product-detail-processing-section';
import ProductDetailTransportSection from './transport/product-detail-transport-section';

export default function ProductDetailPage() {
  const params = useParams();
  const productUuid = params.product_id;
  const navigate = useNavigate();

  const {
    data: productResult,
    isLoading: productResultLoading,
    error: productResultError,
  } = useGetProductResult(productUuid!);

  const {
    data: product,
    isLoading: productLoading,
    error: productError,
  } = useGetProduct(productUuid!);

  const breadcrumb = [
    {
      label: 'Products',
      url: pcfRoutes.PRODUCTS,
    },
    {
      label: product?.name || '',
      url: generatePath(pcfRoutes.PRODUCT_DETAIL, {
        product_id: product?.uuid || '',
      }),
    },
  ];

  const getPageStatus: () => QueryStatus = () => {
    if (productError || productResultError) {
      sentry.log(productError || productResultError);
      return 'error';
    }
    if (productResultLoading || productLoading) {
      return 'loading';
    }
    if (!productResult || !product) {
      return 'error';
    }
    return 'success';
  };

  const ErrorFallback = (
    <PageError
      errorTitle="Unable to load product"
      errorMessage="Please refresh the page or contact us if the error persists!"
    />
  );

  const globalMaxEmissions = useMemo(() => {
    if (!productResult) return 0;
    const ingredientMax = productResult.ingredients.reduce(
      (m, i) => Math.max(m, i.emissions),
      0
    );
    const processingMax = (productResult.processing.processingTypes ?? [])
      .flatMap((t) => t.steps)
      .reduce((m, s) => Math.max(m, s.emissionsPerProduct), 0);
    const packagingMax = (productResult.packaging.packagingTypes ?? [])
      .flatMap((t) => t.items)
      .reduce((m, i) => Math.max(m, i.totalEmissionsPerItem), 0);
    const transportMax = (productResult.transport.transportTypes ?? [])
      .flatMap((t) => t.journeys)
      .reduce((m, j) => Math.max(m, parseFloat(j.totalEmissions)), 0);
    return (
      Math.max(ingredientMax, processingMax, packagingMax, transportMax) * 1.1
    );
  }, [productResult]);

  useEffect(() => {
    if (productResult) {
      productResult.status === StatusEnum.draft &&
        navigate(
          generatePath(pcfRoutes.PRODUCT_EDIT, {
            product_id: productResult.uuid,
          })
        );
    }
  }, [navigate, productResult]);

  const isRemoteProduct = !!productResult?.remoteId;

  return (
    <Page
      name="Product detail"
      status={getPageStatus()}
      title={product?.name}
      fallbackComponent={ErrorFallback}
      className="h-full pb-12"
    >
      {productResult && product && (
        <>
          <PageHeader
            name={
              <ProductDetailName
                name={product.name}
                productUuid={product.uuid}
                canEdit={!isRemoteProduct}
              />
            }
            toolbar={
              <ProductDetailPageToolbar
                product={productResult}
                canEdit={!isRemoteProduct}
              />
            }
            nameTag={
              productResult.status !== StatusEnum.complete
                ? productResult.status === StatusEnum.in_progress
                  ? 'Submitted'
                  : capitalizeFirstLetter(productResult.status!)
                : undefined
            }
            breadcrumbs={breadcrumb}
            nameActions={
              <div className="flex justify-between">
                <ProductDetailCollections
                  productUuid={productResult.uuid}
                  collections={productResult.collections!}
                  canManage={!isRemoteProduct}
                />

                {productResult.remoteId && productResult.remoteUrl && (
                  <Text
                    variant="subtle"
                    className="ml-4 whitespace-nowrap text-xs"
                  >
                    Synced from{' '}
                    <TextLink to={productResult.remoteUrl} internal={false}>
                      {capitalizeFirstLetter(productResult.remoteSource!)}
                    </TextLink>{' '}
                    |{' '}
                    <TextLink to={productResult.remoteUrl} internal={false}>
                      {productResult.remoteId}
                    </TextLink>
                  </Text>
                )}
              </div>
            }
          />

          <div className="mt-2 space-y-3">
            {productResult.status === StatusEnum.editing && (
              <Alert variant="info" title="Editing in progress">
                This product is currently being edited, the results below are
                based on the product data before edits. Complete the assessment
                to view the updated results.
              </Alert>
            )}
            {productResult.assessmentOutdated && (
              <Alert variant="warning" title="Outdated assessment">
                This assessment is outdated, we recommend reassessing the
                product to get the most accurate results.
              </Alert>
            )}
            {productResult.status === StatusEnum.in_progress && (
              <Alert variant="warning" title="In progress">
                This product is currently being assessed.
                {product.hasAssessment &&
                  ' We recommend waiting for the assessment to complete to get the most accurate results.'}{' '}
              </Alert>
            )}
          </div>
          {product.hasAssessment && (
            <>
              {/* Header */}
              <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                <ProductDetailCarbonRatingCard productResult={productResult} />
                <ProductDetailTotalEmissionCard productResult={productResult} />
                <ProductDetailEmissionsOverTime
                  productResult={productResult}
                  className="col-span-full lg:col-span-1"
                />
              </div>

              <div className="mt-6 grid gap-6 md:grid-cols-[2fr_1fr]">
                {/* LHS */}
                <div className="space-y-8">
                  <ProductDetailIngredientsSection
                    product={productResult}
                    globalMaxEmissions={globalMaxEmissions}
                  />
                  <ProductDetailProcessingSection
                    product={productResult}
                    globalMaxEmissions={globalMaxEmissions}
                  />
                  <ProductDetailPackagingSection
                    product={productResult}
                    globalMaxEmissions={globalMaxEmissions}
                  />
                  <ProductDetailTransportSection
                    product={productResult}
                    globalMaxEmissions={globalMaxEmissions}
                  />
                  <ProductDetailLifecycleStageEmissionsSection
                    product={productResult}
                  />
                </div>

                {/* RHS */}
                <div className="col-span-full space-y-4 md:col-span-1">
                  <ProductDetailCommunicateResults
                    productResult={productResult}
                  />

                  <Separator />
                  <ProductComparisonCommand
                    product_a={productResult.uuid}
                    setOpen={() => {}}
                    type="select"
                  />
                  <Separator className="my-3" />
                  <div className="flex">
                    <span className="flex-1">
                      <Text className="font-medium text-primary-500">
                        First assessment
                      </Text>
                      {productResult.firstAssessmentDate && (
                        <Text
                          includeHover
                          hoverText={formatDate(
                            productResult.firstAssessmentDate!,
                            'do MMMM yyyy, h:mm a'
                          )}
                        >
                          {formatDate(productResult.firstAssessmentDate!)}
                        </Text>
                      )}
                    </span>
                    <span className="flex-1">
                      <Text className="font-medium text-primary-500">
                        Last assessment
                      </Text>
                      {productResult.assessmentDate && (
                        <Text
                          includeHover
                          hoverText={formatDate(
                            productResult.assessmentDate!,
                            'do MMMM yyyy, h:mm a'
                          )}
                        >
                          {formatDate(productResult.assessmentDate!)}
                        </Text>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </>
      )}
    </Page>
  );
}
